'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { CheckCircle2, ClipboardList, LifeBuoy, Rocket, Search, type LucideIcon } from 'lucide-react';
import { applyLandingLanguage, useLandingLanguage } from '@/hooks/useLandingLanguage';
import { useEffect } from 'react';
import { CadrinoShell, Container, Section, SectionHeader, MotionBlock, Card, ContentCard } from './shared';
import { CadrinoMarketingHeader } from './CadrinoMarketingHeader';
import { CadrinoFooter } from './CadrinoFooter';
import { MarketingHero } from './MarketingHero';
import { ContactSection } from './ContactSection';
import { howWeWorkPageContent } from '@/lib/cadrino-landing/how-we-work-page-content';
const stepIcons: LucideIcon[] = [Search, ClipboardList, Rocket, CheckCircle2, LifeBuoy];
export function CadrinoHowWeWorkPage() {
  const $ = _c(58);
  const {
    language
  } = useLandingLanguage();
  const lang = language === "en" ? "en" : "sr";
  const page = howWeWorkPageContent[lang];
  let t0;
  let t1;
  if ($[0] !== language) {
    t0 = () => {
      applyLandingLanguage(language);
    };
    t1 = [language];
    $[0] = language;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CadrinoMarketingHeader />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== page.hero.eyebrow || $[5] !== page.hero.highlights || $[6] !== page.hero.primaryCta || $[7] !== page.hero.secondaryCta || $[8] !== page.hero.secondaryHref || $[9] !== page.hero.subtitle || $[10] !== page.hero.title) {
    t3 = <MarketingHero variant="inner" eyebrow={page.hero.eyebrow} title={page.hero.title} subtitle={page.hero.subtitle} highlights={page.hero.highlights} primaryCtaLabel={page.hero.primaryCta} primaryCtaHref="#contact" secondaryCtaLabel={page.hero.secondaryCta} secondaryCtaHref={page.hero.secondaryHref} />;
    $[4] = page.hero.eyebrow;
    $[5] = page.hero.highlights;
    $[6] = page.hero.primaryCta;
    $[7] = page.hero.secondaryCta;
    $[8] = page.hero.secondaryHref;
    $[9] = page.hero.subtitle;
    $[10] = page.hero.title;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== page.sections.timeline.subtitle || $[13] !== page.sections.timeline.title) {
    t4 = <MotionBlock><SectionHeader title={page.sections.timeline.title} subtitle={page.sections.timeline.subtitle} className="max-w-3xl" /></MotionBlock>;
    $[12] = page.sections.timeline.subtitle;
    $[13] = page.sections.timeline.title;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== page.sections.timeline.steps) {
    let t6;
    if ($[17] !== page.sections.timeline.steps.length) {
      t6 = (step, idx) => {
        const Icon = stepIcons[idx] ?? Search;
        const isLast = idx === page.sections.timeline.steps.length - 1;
        return <MotionBlock key={step.title} delay={idx * 0.06}><li className="relative flex gap-4 sm:gap-5">{!isLast && <span className="absolute left-[1.35rem] top-14 bottom-0 w-px bg-gradient-to-b from-cadrino-primary/25 via-slate-200 to-transparent" aria-hidden={true} />}<div className="relative z-10 mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white bg-cadrino-primary text-white shadow-md shadow-cadrino-primary/20 ring-4 ring-cadrino-primary/10"><Icon className="h-5 w-5" aria-hidden={true} /></div><motion.div whileHover={{
              y: -2
            }} transition={{
              duration: 0.2,
              ease: "easeOut"
            }} className="flex-1"><ContentCard className="flex-1 border-white/[0.12] bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5 shadow-sm transition-shadow hover:shadow-md sm:p-6"><div className="flex flex-wrap items-center gap-2"><span className="rounded-full border border-cadrino-accent/25 bg-cadrino-accent/10 px-2.5 py-0.5 text-xs font-medium text-cadrino-accent">{step.duration}</span></div><h3 className="mt-2 text-lg font-semibold text-slate-100">{step.title}</h3><p className="mt-2 text-sm leading-relaxed text-cadrino-muted">{step.description}</p><ul className="mt-4 grid gap-2 sm:grid-cols-3">{step.deliverables.map(_temp)}</ul></ContentCard></motion.div></li></MotionBlock>;
      };
      $[17] = page.sections.timeline.steps.length;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t5 = page.sections.timeline.steps.map(t6);
    $[15] = page.sections.timeline.steps;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[19] !== t5) {
    t6 = <ol className="mt-10 space-y-5">{t5}</ol>;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t6) {
    t7 = <Section className="border-t border-white/[0.08]"><Container>{t4}{t6}</Container></Section>;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== page.sections.visibility.subtitle || $[25] !== page.sections.visibility.title) {
    t8 = <MotionBlock><SectionHeader title={page.sections.visibility.title} subtitle={page.sections.visibility.subtitle} className="max-w-3xl" /></MotionBlock>;
    $[24] = page.sections.visibility.subtitle;
    $[25] = page.sections.visibility.title;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== page.sections.visibility.metrics) {
    t9 = page.sections.visibility.metrics.map(_temp2);
    $[27] = page.sections.visibility.metrics;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <div className="mt-8 grid gap-4 md:grid-cols-3">{t9}</div>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t8) {
    t11 = <Section className="border-t border-white/[0.08] bg-cadrino-surface/35"><Container>{t8}{t10}</Container></Section>;
    $[31] = t10;
    $[32] = t8;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== page.sections.expectations.subtitle || $[35] !== page.sections.expectations.title) {
    t12 = <MotionBlock><SectionHeader title={page.sections.expectations.title} subtitle={page.sections.expectations.subtitle} className="max-w-3xl" /></MotionBlock>;
    $[34] = page.sections.expectations.subtitle;
    $[35] = page.sections.expectations.title;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== page.sections.expectations.cards) {
    t13 = page.sections.expectations.cards.map(_temp3);
    $[37] = page.sections.expectations.cards;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13) {
    t14 = <div className="mt-10 grid gap-4 md:grid-cols-3">{t13}</div>;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t12 || $[42] !== t14) {
    t15 = <Section className="border-t border-white/[0.08]"><Container>{t12}{t14}</Container></Section>;
    $[41] = t12;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== page.finalCta.primaryLabel || $[45] !== page.finalCta.qualifierChips || $[46] !== page.finalCta.subtitle || $[47] !== page.finalCta.title) {
    t16 = <ContactSection title={page.finalCta.title} subtitle={page.finalCta.subtitle} primaryLabel={page.finalCta.primaryLabel} primaryHref="#contact-form" qualifierChips={page.finalCta.qualifierChips} />;
    $[44] = page.finalCta.primaryLabel;
    $[45] = page.finalCta.qualifierChips;
    $[46] = page.finalCta.subtitle;
    $[47] = page.finalCta.title;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t11 || $[50] !== t15 || $[51] !== t16 || $[52] !== t3 || $[53] !== t7) {
    t17 = <main>{t3}{t7}{t11}{t15}{t16}</main>;
    $[49] = t11;
    $[50] = t15;
    $[51] = t16;
    $[52] = t3;
    $[53] = t7;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <CadrinoFooter />;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t17) {
    t19 = <CadrinoShell>{t2}{t17}{t18}</CadrinoShell>;
    $[56] = t17;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  return t19;
}
function _temp3(card, idx_1) {
  return <MotionBlock key={card.title} delay={idx_1 * 0.06}><ContentCard className="h-full p-6"><h3 className="text-lg font-semibold text-slate-100">{card.title}</h3><p className="mt-3 text-sm leading-relaxed text-cadrino-muted">{card.description}</p></ContentCard></MotionBlock>;
}
function _temp2(metric, idx_0) {
  return <MotionBlock key={metric.label} delay={idx_0 * 0.06}><Card className="h-full border-cadrino-primary/20 bg-white/[0.04] p-5"><p className="text-xs uppercase tracking-wider text-cadrino-accent">{metric.label}</p><p className="mt-2 text-3xl font-semibold text-slate-100">{metric.display}</p><div className="mt-3 h-2 overflow-hidden rounded-full bg-white/[0.08]"><motion.div className="h-full rounded-full bg-gradient-to-r from-cadrino-primary to-cadrino-accent" initial={{
          width: 0
        }} whileInView={{
          width: `${metric.progress}%`
        }} viewport={{
          once: true,
          amount: 0.4
        }} transition={{
          duration: 0.9,
          delay: idx_0 * 0.08,
          ease: "easeOut"
        }} /></div><p className="mt-3 text-sm leading-relaxed text-cadrino-muted">{metric.hint}</p></Card></MotionBlock>;
}
function _temp(item) {
  return <li key={item} className="rounded-lg border border-white/[0.1] bg-white/[0.04] px-3 py-2 text-xs text-slate-300 transition-colors hover:bg-white/[0.08]">{item}</li>;
}